import { useEffect, useState } from 'react';
import { api } from '../../shared/api/client';
import type { LiveState } from '../../shared/api/types';

/** Cada cuánto se vuelve a preguntar si la iglesia está en vivo (el servidor revisa YouTube cada minuto). */
const POLL_MS = 60_000;

/**
 * El estado del reproductor de la iglesia, al día: se vuelve a pedir cada minuto y al regresar a la pestaña,
 * así el directo aparece solo y se quita solo. null mientras carga o si no se pudo leer.
 */
export function useLiveState(path: string | null): LiveState | null {
  const [state, setState] = useState<LiveState | null>(null);
  useEffect(() => {
    setState(null);
    if (!path) return;
    let controller = new AbortController();
    const load = () => {
      controller.abort();
      controller = new AbortController();
      api
        .get<LiveState>(path, controller.signal)
        .then((s) => setState((prev) => (prev && sameState(prev, s) ? prev : s)))
        .catch(() => undefined);
    };
    load();
    const timer = setInterval(load, POLL_MS);
    const onVisible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      controller.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [path]);
  return state;
}

function sameState(a: LiveState, b: LiveState): boolean {
  return a.status === b.status && a.kind === b.kind && a.videoId === b.videoId && a.url === b.url && a.videos.join() === b.videos.join();
}

/** La dirección para incrustar lo que toca (siempre sin sonido al empezar: los navegadores no dejan otra cosa). */
export function embedFor(s: LiveState): { src: string; tall: boolean } | null {
  const yt = (id: string, extra = '') => `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&playsinline=1&rel=0${extra}`;
  if (s.status === 'live') {
    if (s.kind === 'youtube' && s.videoId) return { src: yt(s.videoId), tall: false };
    if (s.kind === 'facebook' && s.url) return { src: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(s.url)}&show_text=false&autoplay=true&mute=true`, tall: false };
    if (s.kind === 'facebook-page' && s.url) {
      return { src: `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(s.url)}&tabs=timeline&width=500&height=640&small_header=true&adapt_container_width=true`, tall: true };
    }
    return null;
  }
  if (s.status === 'loop' && s.videos.length) return { src: yt(s.videos[0]!, `&loop=1&playlist=${s.videos.join(',')}`), tall: false };
  return null;
}

/** El reproductor incrustado. No muestra nada si no hay directo ni videos. */
export function LivePlayer({ state, title }: { state: LiveState; title: string }) {
  const embed = embedFor(state);
  if (!embed) return null;
  return (
    <div className={embed.tall ? 'video video--tall' : 'video'}>
      <iframe
        key={embed.src}
        src={embed.src}
        title={state.status === 'live' ? `En vivo · ${title}` : title}
        allow="autoplay; accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    </div>
  );
}

/** «● En vivo» o, sin directo, de qué se trata lo que se está viendo. */
export function LiveBadge({ state }: { state: LiveState }) {
  return state.status === 'live' ? <span className="wall-live">● En vivo</span> : <span>Mientras volvemos a transmitir</span>;
}
